import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./ResearchInfoTabs.css";

const RATING_COLORS = { High: "#22c55e", Medium: "#f59e0b", Low: "#94a3b8" };

const scoreColor = (v, hi = 70, mid = 40) =>
  v >= hi ? "#22c55e" : v >= mid ? "#f59e0b" : "#94a3b8";

/**
 * Coerce any value into a renderable string. Research/signal fields are
 * sometimes objects (e.g. geoHint = { country, city }) or arrays, which React
 * cannot render directly — this flattens them into readable text.
 */
const asText = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(", ");
  if (typeof v === "object") {
    return Object.values(v)
      .map(asText)
      .filter(Boolean)
      .join(", ");
  }
  return String(v);
};

// First sentence (or a trimmed snippet) of a longer text — used for the
// always-visible collapsed preview on filled cards.
const firstSentence = (text, max = 150) => {
  const t = asText(text).trim();
  if (!t) return "";
  const match = t.match(/^[\s\S]*?[.!?](\s|$)/);
  let s = (match ? match[0] : t).trim();
  if (s.length > max) s = s.slice(0, max).trim().replace(/[\s,;:.]+$/, "") + "…";
  return s;
};

const safeParse = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (_) {
    return null;
  }
};

const buildSocialHref = (platform, val) => {
  if (!val || !val.trim()) return null;
  const v = val.trim();
  if (v.startsWith("http")) return v;
  const domains = {
    linkedin: "linkedin.com/in",
    facebook: "facebook.com",
    instagram: "instagram.com",
    x: "x.com",
    twitter: "x.com",
    tiktok: "tiktok.com/@",
    youtube: "youtube.com/@",
    xing: "xing.com/profile",
  };
  return domains[platform]
    ? `https://${domains[platform]}/${v.replace("@", "")}`
    : `https://${v}`;
};

const SOCIAL_ICONS = {
  linkedin: "fab fa-linkedin",
  facebook: "fab fa-facebook",
  instagram: "fab fa-instagram",
  x: "fab fa-x-twitter",
  twitter: "fab fa-x-twitter",
  tiktok: "fab fa-tiktok",
  youtube: "fab fa-youtube",
  xing: "fab fa-xing",
};

/**
 * ResearchInfoTabs
 * Toggleable tab strip showing structured research data (Web Research,
 * Decision Makers, Instagram, SEO, Signals) for a single contact.
 *
 * Reads from `contact.researchIntelligence`, `contact.lead_decision_makers`
 * (JSON), `contact.leadSignals` and a few contact-level score fields. Renders
 * nothing when the contact has no structured data, so contacts that were never
 * researched stay clean.
 */
const ResearchInfoTabs = ({ contact }) => {
  const [openCard, setOpenCard] = useState(null);

  const ri = useMemo(() => contact?.researchIntelligence || {}, [contact]);
  const decisionMakers = useMemo(() => {
    const parsed = safeParse(contact?.lead_decision_makers);
    return Array.isArray(parsed) ? parsed.filter((dm) => dm && (dm.name || dm.role || dm.email)) : [];
  }, [contact]);

  const igInsight = ri.instagramInsight || null;
  const seo = ri.seoAnalysis || null;

  const potentialScore =
    typeof contact?.potential_score === "number" && !Number.isNaN(contact.potential_score)
      ? contact.potential_score
      : null;
  const potentialAnalysis = safeParse(contact?.potential_analysis) || {};
  const potentialBullets = Array.isArray(potentialAnalysis.bullets)
    ? potentialAnalysis.bullets.filter((b) => asText(b).trim())
    : [];
  const hasPotential =
    potentialScore != null || potentialAnalysis.summary || potentialAnalysis.fitLabel || potentialBullets.length > 0;
  const potentialNoInfo = potentialAnalysis.dataConfidence === "none";
  const fitTier = potentialScore != null
    ? (potentialNoInfo ? "nodata" : (potentialScore >= 6 ? "high" : potentialScore >= 3 ? "mid" : "low"))
    : null;

  const leadScore = contact?.lead_score ? parseInt(contact.lead_score, 10) : ri.leadScore || null;
  const spyConf = contact?.spy_confidence_score
    ? parseInt(contact.spy_confidence_score, 10)
    : ri.confidenceScore || null;

  const hasResearch =
    ri.version != null ||
    ri.industryFit ||
    ri.companyInfo ||
    ri.budgetIndicators ||
    ri.reasoning ||
    ri.companySizeEstimate ||
    ri.onlinePresenceScore != null;

  const cards = useMemo(() => {
    const list = [];
    if (hasResearch) {
      const qs = ri.quickStats || {};
      const shopPreview = ri.shopIntelligence && typeof ri.shopIntelligence === "object" ? ri.shopIntelligence : null;
      const liPreview = ri.linkedinCompany && typeof ri.linkedinCompany === "object" ? ri.linkedinCompany : null;
      const researchPills = [
        qs.employees && { icon: "fa fa-users", value: asText(qs.employees) },
        qs.revenue && { icon: "fa fa-euro-sign", value: asText(qs.revenue) },
        qs.founded && { icon: "fa fa-calendar", value: asText(qs.founded) },
        qs.location && { icon: "fa fa-location-dot", value: asText(qs.location) },
        shopPreview && shopPreview.platform && { icon: "fa fa-cart-shopping", value: asText(shopPreview.platform) },
        liPreview && liPreview.employeesFound > 0 && { icon: "fab fa-linkedin", value: `${liPreview.employeesFound}` },
        ri.onlinePresenceScore != null && { icon: "fa fa-globe", value: `${ri.onlinePresenceScore}%` },
      ].filter(Boolean);
      const researchSnippetText = firstSentence(
        ri.companyInfo || (Array.isArray(ri.keyFacts) && ri.keyFacts[0]) || ri.industryFit
      );
      list.push({
        id: "research",
        label: "Web Research",
        icon: "fa fa-microscope",
        iconImg: "/img/star.svg",
        preview: ri.leadRating ? (
          <span
            className="bcResearch-chip"
            style={{ color: RATING_COLORS[ri.leadRating] || "#94a3b8" }}
          >
            <i className="fa fa-circle" />
            {ri.leadRating}
            {ri.leadScore != null ? ` ${ri.leadScore}` : ""}
          </span>
        ) : null,
        snippet:
          researchPills.length > 0 || researchSnippetText ? (
            <>
              {researchPills.length > 0 && <QuickPills items={researchPills} />}
              {researchSnippetText && <p className="bcResearch-snippetText">{researchSnippetText}</p>}
            </>
          ) : null,
        render: () => (
          <ResearchTab ri={ri} leadScore={leadScore} spyConf={spyConf} researchedAt={formatDate(ri.researchedAt)} />
        ),
      });
    }
    if (hasPotential) {
      list.push({
        id: "fit",
        label: "Fit",
        icon: "fa fa-bullseye",
        preview: (
          <span className="bcResearch-fitPreview">
            {potentialScore != null && (
              <span className={`bcResearch-fitBadge bcResearch-fitBadge--${fitTier}`}>
                {potentialNoInfo && <i className="fa fa-question" style={{ marginRight: 4 }} />}{potentialScore}/10
              </span>
            )}
            {(potentialNoInfo || potentialAnalysis.fitLabel) && (
              <span className="bcResearch-fitPreviewLabel">{potentialNoInfo ? "Keine Infos" : asText(potentialAnalysis.fitLabel)}</span>
            )}
          </span>
        ),
        snippet: potentialAnalysis.summary ? (
          <p className="bcResearch-snippetText">{firstSentence(potentialAnalysis.summary)}</p>
        ) : null,
        render: () => (
          <FitTab
            score={potentialScore}
            fitLabel={potentialAnalysis.fitLabel}
            summary={potentialAnalysis.summary}
            bullets={potentialBullets}
            noInfo={potentialNoInfo}
          />
        ),
      });
    }
    if (decisionMakers.length > 0) {
      list.push({
        id: "dms",
        label: "Decision Makers",
        icon: "fa fa-user-tie",
        preview: <span className="bcResearch-countPill">{decisionMakers.length}</span>,
        render: () => <DecisionMakersTab decisionMakers={decisionMakers} />,
      });
    }
    if (igInsight) {
      list.push({
        id: "instagram",
        label: "Instagram",
        icon: "fab fa-instagram",
        preview:
          igInsight.activityScore != null ? (
            <span className="bcResearch-chip">
              <i className="fa fa-bolt" />
              {igInsight.activityScore}/10
            </span>
          ) : null,
        render: () => <InstagramTab ig={igInsight} />,
      });
    }
    if (seo && seo.data) {
      list.push({
        id: "seo",
        label: "SEO",
        icon: "fa fa-magnifying-glass-chart",
        preview:
          seo.data.core?.visibility?.value != null ? (
            <span className="bcResearch-chip">Vis {Number(seo.data.core.visibility.value).toFixed(2)}</span>
          ) : null,
        render: () => <SeoTab seo={seo} />,
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResearch, hasPotential, decisionMakers, igInsight, seo, leadScore, spyConf, potentialScore, fitTier, potentialNoInfo]);

  if (cards.length === 0) return null;

  const toggleCard = (id) => setOpenCard((prev) => (prev === id ? null : id));
  const anyOpen = openCard !== null;

  return (
    <div className="bcResearch">
      {cards.map((card, index) => {
        const isOpen = openCard === card.id;
        return (
          <div
            key={card.id}
            className={`bcResearch-card ${isOpen ? "is-open" : ""} ${card.snippet ? "has-snippet" : ""}`}
            style={{ zIndex: isOpen ? 50 : index + 1 }}
          >
            <button
              type="button"
              className="bcResearch-cardHead"
              onClick={() => toggleCard(card.id)}
              aria-expanded={isOpen}
            >
              <span className="bcResearch-cardIcon">
                {card.iconImg ? (
                  <span
                    className="bcResearch-cardIconImg"
                    style={{
                      WebkitMaskImage: `url(${card.iconImg})`,
                      maskImage: `url(${card.iconImg})`,
                    }}
                  />
                ) : (
                  <i className={card.icon} />
                )}
              </span>
              <span className="bcResearch-cardTitle">{card.label}</span>
              <span className="bcResearch-cardPreview">{card.preview}</span>
              <i className="bcResearch-cardChevron fa fa-chevron-down" />
            </button>
            <AnimatePresence initial={false}>
              {!isOpen && !anyOpen && card.snippet && (
                <motion.div
                  key="snippet"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { type: "spring", stiffness: 420, damping: 38, mass: 0.7 },
                    opacity: { duration: 0.18, ease: "easeOut" },
                  }}
                  style={{ overflow: "hidden" }}
                >
                  <div className="bcResearch-cardSnippet" onClick={() => toggleCard(card.id)}>
                    {card.snippet}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="body"
                  className="bcResearch-cardBodyWrap"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { type: "spring", stiffness: 380, damping: 36, mass: 0.8 },
                    opacity: { duration: 0.2, ease: "easeOut" },
                  }}
                  style={{ overflow: "hidden" }}
                >
                  <div className="bcResearch-cardBody">{card.render()}</div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

const QuickPills = ({ items }) => (
  <div className="bcResearch-pills">
    {items.map((p, i) => (
      <span key={i} className="bcResearch-pill">
        <i className={p.icon} />
        {p.value}
      </span>
    ))}
  </div>
);

const FitTab = ({ score, fitLabel, summary, bullets, noInfo }) => {
  const tier = score != null
    ? (noInfo ? "nodata" : (score >= 6 ? "high" : score >= 3 ? "mid" : "low"))
    : null;
  return (
    <div className="bcResearchTabs-content">
      <div className="bcResearchTabs-fitHead">
        {score != null && (
          <span className={`bcResearchTabs-fitScore bcResearchTabs-fitScore--${tier}`}>
            {noInfo && <i className="fa fa-question" style={{ marginRight: 4 }} />}{score}/10
          </span>
        )}
        {(noInfo || fitLabel) && <span className="bcResearchTabs-fitLabel">{noInfo ? "Keine Infos gefunden" : asText(fitLabel)}</span>}
      </div>
      {noInfo && (
        <p className="bcResearchTabs-fitSummary" style={{ opacity: 0.85 }}>
          <i className="fa fa-circle-info" style={{ marginRight: 6 }} />
          Im Web konnten kaum verwertbare Infos gefunden werden. Das wurde als Nachteil eingerechnet (kein neutraler Wert).
        </p>
      )}
      {summary && <p className="bcResearchTabs-fitSummary">{asText(summary)}</p>}
      {bullets.length > 0 && (
        <div className="bcResearchTabs-detail">
          <span className="bcResearchTabs-label">
            <i className="fa fa-list-check" /> Hauptgründe
          </span>
          <ul className="bcResearchTabs-fitReasons">
            {bullets.map((b, i) => (
              <li key={i}>
                <i className="fa fa-circle-check" />
                <span>{asText(b)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ResearchTab = ({ ri, leadScore, spyConf, researchedAt }) => {
  const ratingColor = ri.leadRating ? RATING_COLORS[ri.leadRating] || "#94a3b8" : null;
  const keyFacts = Array.isArray(ri.keyFacts) ? ri.keyFacts.filter((f) => asText(f).trim()) : [];
  const buySignals = Array.isArray(ri.buySignals) ? ri.buySignals.filter((s) => asText(s.signal || s).trim()) : [];
  const latestNews = Array.isArray(ri.latestNews) ? ri.latestNews.filter((n) => n && asText(n.headline).trim()) : [];

  const shop = ri.shopIntelligence && typeof ri.shopIntelligence === "object" ? ri.shopIntelligence : null;
  const liCompany = ri.linkedinCompany && typeof ri.linkedinCompany === "object" ? ri.linkedinCompany : null;
  const shopMeta = shop
    ? [
        shop.platform && { icon: "fa fa-store", label: "Platform", value: asText(shop.platform) },
        shop.productCount && { icon: "fa fa-box", label: "Products", value: asText(shop.productCount) },
        shop.founded && { icon: "fa fa-calendar", label: "Founded", value: asText(shop.founded) },
        shop.trustScore &&
          shop.trustScore.score && {
            icon: "fa fa-star",
            label: asText(shop.trustScore.source) || "Rating",
            value: asText(shop.trustScore.score),
          },
      ].filter(Boolean)
    : [];

  const qs = ri.quickStats && typeof ri.quickStats === "object" ? ri.quickStats : {};
  const companyStats = [
    qs.employees && { icon: "fa fa-users", label: "Employees", value: asText(qs.employees) },
    qs.revenue && { icon: "fa fa-euro-sign", label: "Revenue", value: asText(qs.revenue) },
    qs.founded && { icon: "fa fa-calendar", label: "Founded", value: asText(qs.founded) },
    qs.location && { icon: "fa fa-location-dot", label: "HQ", value: asText(qs.location) },
  ].filter(Boolean);

  const quickFacts = [
    ri.industryFit && { label: "Industry Fit", value: asText(ri.industryFit), wide: true },
    ri.companySizeEstimate && { label: "Est. Size", value: asText(ri.companySizeEstimate) },
    ri.leadRating && {
      label: "Rating",
      value: `${ri.leadRating}${ri.leadScore != null ? ` · ${ri.leadScore}/100` : ""}`,
      color: ratingColor,
    },
  ].filter(Boolean);

  const hasBasics =
    quickFacts.length > 0 || keyFacts.length > 0 || companyStats.length > 0 || ri.onlinePresenceScore != null;

  return (
    <div className="bcResearchTabs-content bcResearch-sections">
      {hasBasics && (
        <section className="bcResearch-section">
          <div className="bcResearch-secHead">
            <i className="fa fa-circle-info" /> Basics
          </div>
          {companyStats.length > 0 && (
            <div className="bcResearch-statGrid">
              {companyStats.map((s, i) => (
                <div key={i} className="bcResearch-stat">
                  <i className={s.icon} />
                  <div className="bcResearch-statText">
                    <span className="bcResearch-statLabel">{s.label}</span>
                    <span className="bcResearch-statValue">{s.value}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {(quickFacts.length > 0 || ri.onlinePresenceScore != null) && (
            <div className="bcResearchTabs-grid">
              {quickFacts.map((f, i) => (
                <div key={i} className={`bcResearchTabs-item ${f.wide ? "bcResearchTabs-item--wide" : ""}`}>
                  <span className="bcResearchTabs-label">{f.label}</span>
                  <span className="bcResearchTabs-value" style={f.color ? { color: f.color } : undefined}>
                    {f.value}
                  </span>
                </div>
              ))}
              {ri.onlinePresenceScore != null && (
                <div className="bcResearchTabs-item">
                  <span className="bcResearchTabs-label">Online Presence</span>
                  <div className="bcResearchTabs-meter">
                    <div
                      className="bcResearchTabs-meterFill"
                      style={{ width: `${ri.onlinePresenceScore}%`, background: scoreColor(ri.onlinePresenceScore) }}
                    />
                  </div>
                  <span className="bcResearchTabs-value">{ri.onlinePresenceScore}</span>
                </div>
              )}
            </div>
          )}
          {keyFacts.length > 0 && (
            <ul className="bcResearch-factList">
              {keyFacts.map((f, i) => (
                <li key={i}>
                  <i className="fa fa-circle" />
                  <span>{asText(f)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {buySignals.length > 0 && (
        <section className="bcResearch-section">
          <div className="bcResearch-secHead bcResearch-secHead--signal">
            <i className="fa fa-bolt" /> Buy Signals
          </div>
          <div className="bcResearch-signalList">
            {buySignals.map((s, i) => (
              <div key={i} className="bcResearch-signal">
                <i className="fa fa-arrow-trend-up" />
                <div className="bcResearch-signalBody">
                  <span className="bcResearch-signalText">{asText(s.signal || s)}</span>
                  {s.evidence && <span className="bcResearch-signalEvidence">{asText(s.evidence)}</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {latestNews.length > 0 && (
        <section className="bcResearch-section">
          <div className="bcResearch-secHead bcResearch-secHead--news">
            <i className="fa fa-newspaper" /> Latest News
          </div>
          <div className="bcResearch-newsList">
            {latestNews.map((n, i) => (
              <div key={i} className="bcResearch-news">
                <div className="bcResearch-newsTop">
                  <span className="bcResearch-newsHeadline">{asText(n.headline)}</span>
                  {n.date && <span className="bcResearch-newsDate">{asText(n.date)}</span>}
                </div>
                {n.hookValue && (
                  <span className="bcResearch-newsHook">
                    <i className="fa fa-lightbulb" />
                    {asText(n.hookValue)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {shop && (shopMeta.length > 0 || shop.summary || (shop.categories || []).length > 0 || (shop.brands || []).length > 0) && (
        <section className="bcResearch-section">
          <div className="bcResearch-secHead bcResearch-secHead--shop">
            <i className="fa fa-cart-shopping" /> Online Shop
          </div>
          {shopMeta.length > 0 && (
            <div className="bcResearch-statGrid">
              {shopMeta.map((s, i) => (
                <div key={i} className="bcResearch-stat">
                  <i className={s.icon} />
                  <div className="bcResearch-statText">
                    <span className="bcResearch-statLabel">{s.label}</span>
                    <span className="bcResearch-statValue">{s.value}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {Array.isArray(shop.categories) && shop.categories.length > 0 && (
            <div className="bcResearch-tagRow">
              {shop.categories.map((c, i) => (
                <span key={i} className="bcResearch-tag">{asText(c)}</span>
              ))}
            </div>
          )}
          {Array.isArray(shop.brands) && shop.brands.length > 0 && (
            <div className="bcResearch-tagRow">
              {shop.brands.map((b, i) => (
                <span key={i} className="bcResearch-tag bcResearch-tag--brand">{asText(b)}</span>
              ))}
            </div>
          )}
          {shop.summary && <p className="bcResearch-narrative">{asText(shop.summary)}</p>}
        </section>
      )}

      {ri.companyInfo && (
        <section className="bcResearch-section">
          <div className="bcResearch-secHead">
            <i className="fa fa-building" /> Company
          </div>
          <p className="bcResearch-narrative">{asText(ri.companyInfo)}</p>
        </section>
      )}

      {liCompany && liCompany.url && (
        <section className="bcResearch-section">
          <div className="bcResearch-secHead bcResearch-secHead--linkedin">
            <i className="fab fa-linkedin" /> LinkedIn
          </div>
          <a className="bcResearch-linkedinCard" href={liCompany.url} target="_blank" rel="noopener noreferrer">
            <div className="bcResearch-linkedinTop">
              <span className="bcResearch-linkedinName">{asText(liCompany.name) || "Company Profile"}</span>
              {liCompany.employeesFound > 0 && (
                <span className="bcResearch-linkedinEmp">
                  <i className="fa fa-users" /> {liCompany.employeesFound}
                </span>
              )}
            </div>
            {liCompany.description && (
              <span className="bcResearch-linkedinDesc">{asText(liCompany.description)}</span>
            )}
            <span className="bcResearch-linkedinUrl">
              <i className="fa fa-arrow-up-right-from-square" /> {asText(liCompany.url)}
            </span>
          </a>
        </section>
      )}

      {ri.budgetIndicators && buySignals.length === 0 && (
        <section className="bcResearch-section">
          <div className="bcResearch-secHead bcResearch-secHead--signal">
            <i className="fa fa-coins" /> Budget Indicators
          </div>
          <p className="bcResearch-narrative">{asText(ri.budgetIndicators)}</p>
        </section>
      )}

      {ri.reasoning && (
        <section className="bcResearch-section">
          <div className="bcResearch-secHead">
            <i className="fa fa-brain" /> AI Reasoning
          </div>
          <p className="bcResearch-narrative">{asText(ri.reasoning)}</p>
        </section>
      )}

      {(leadScore != null || spyConf != null || researchedAt) && (
        <div className="bcResearch-footer">
          {leadScore != null && (
            <span className="bcResearchTabs-scoreChip">
              <i className="fa fa-star" /> Lead Score {leadScore}
            </span>
          )}
          {spyConf != null && (
            <span className="bcResearchTabs-scoreChip">
              <i className="fa fa-check-double" /> Confidence {spyConf}%
            </span>
          )}
          {researchedAt && (
            <span className="bcResearch-footerDate">
              <i className="fa fa-clock" /> {researchedAt}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const DecisionMakersTab = ({ decisionMakers }) => (
  <div className="bcResearchTabs-content">
    <div className="bcResearchTabs-dmList">
      {decisionMakers.map((dm, idx) => {
        const socials = dm.socials && typeof dm.socials === "object" ? dm.socials : {};
        return (
          <div key={idx} className="bcResearchTabs-dmCard">
            <div className="bcResearchTabs-dmAvatar">
              <i className="fa fa-user-tie" />
            </div>
            <div className="bcResearchTabs-dmInfo">
              <span className="bcResearchTabs-dmName">{asText(dm.name) || "Unknown"}</span>
              {(dm.role || dm.position) && (
                <span className="bcResearchTabs-dmRole">{asText(dm.role || dm.position)}</span>
              )}
              {dm.email && (
                <a href={`mailto:${dm.email}`} className="bcResearchTabs-dmContact">
                  <i className="fa fa-envelope" />
                  {dm.email}
                  {dm.emailVerified && <i className="fa fa-check-circle bcResearchTabs-dmVerified" />}
                </a>
              )}
              {dm.phone && (
                <a href={`tel:${dm.phone}`} className="bcResearchTabs-dmContact">
                  <i className="fa fa-phone" />
                  {dm.phone}
                </a>
              )}
            </div>
            <div className="bcResearchTabs-dmSocials">
              {Object.entries(socials).map(([platform, url]) => {
                const href = buildSocialHref(platform, url);
                const icon = SOCIAL_ICONS[platform];
                if (!href || !icon) return null;
                return (
                  <a
                    key={platform}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bcResearchTabs-dmSocial"
                    title={platform}
                  >
                    <i className={icon} />
                  </a>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const InstagramTab = ({ ig }) => {
  if (!ig) return null;
  return (
    <div className="bcResearchTabs-content">
      {ig.summary && <p className="bcResearchTabs-igSummary">{asText(ig.summary)}</p>}
      <div className="bcResearchTabs-badges">
        {ig.activityScore != null && (
          <span className="bcResearchTabs-badge">
            <i className="fa fa-bolt" /> Activity {ig.activityScore}/10
          </span>
        )}
        {ig.engagementScore != null && (
          <span className="bcResearchTabs-badge">
            <i className="fa fa-fire" /> Engagement {ig.engagementScore}/10
          </span>
        )}
        {ig.tone && (
          <span className="bcResearchTabs-badge">
            <i className="fa fa-comment-alt" /> {ig.tone}
          </span>
        )}
        {ig.avgPostsPerWeek != null && (
          <span className="bcResearchTabs-badge">
            <i className="fa fa-calendar-alt" /> {ig.avgPostsPerWeek}/week
          </span>
        )}
        {ig.avgLikes != null && (
          <span className="bcResearchTabs-badge">
            <i className="fa fa-heart" /> ~{ig.avgLikes} avg
          </span>
        )}
      </div>
      {Array.isArray(ig.uniqueFacts) && ig.uniqueFacts.length > 0 && (
        <div className="bcResearchTabs-detail">
          <span className="bcResearchTabs-label">
            <i className="fa fa-star" /> Key Facts
          </span>
          <span className="bcResearchTabs-value">{ig.uniqueFacts.map(asText).filter(Boolean).join(" · ")}</span>
        </div>
      )}
      {ig.salesAngle && (
        <div className="bcResearchTabs-salesAngle">
          <i className="fa fa-lightbulb" />
          <span>{asText(ig.salesAngle)}</span>
        </div>
      )}
    </div>
  );
};

const SeoTab = ({ seo }) => {
  if (!seo || !seo.data) return null;
  const core = seo.data.core || {};
  const tv = seo.data.trafficValue || null;
  const vt = seo.data.visibilityTrend || null;
  const metrics = [
    core.visibility?.value != null && {
      label: "Visibility",
      value: Number(core.visibility.value).toFixed(2),
    },
    core.keywordsTotal != null && {
      label: "Keywords",
      value: Number(core.keywordsTotal).toLocaleString("de-DE"),
    },
    core.keywordsTop10 != null && {
      label: "Top 10",
      value: Number(core.keywordsTop10).toLocaleString("de-DE"),
    },
    tv?.estimatedTrafficMonthly != null && {
      label: "Visitors/mo",
      value: `~${Number(tv.estimatedTrafficMonthly).toLocaleString("de-DE")}`,
    },
    tv?.estimatedValueMonthly ? {
      label: "Traffic Value",
      value: `~${Number(tv.estimatedValueMonthly).toLocaleString("de-DE")} €`,
    } : null,
    seo.data.backlinks?.total != null && {
      label: "Backlinks",
      value: Number(seo.data.backlinks.total).toLocaleString("de-DE"),
    },
    vt?.changePct != null && {
      label: `Trend ${vt.overWindow || "12M"}`,
      value: `${vt.changePct > 0 ? "+" : ""}${vt.changePct}%`,
      color: vt.direction === "down" ? "#dc2626" : vt.direction === "up" ? "#16a34a" : undefined,
    },
  ].filter(Boolean);

  return (
    <div className="bcResearchTabs-content">
      <div className="bcResearchTabs-grid">
        {metrics.map((m, i) => (
          <div key={i} className="bcResearchTabs-item">
            <span className="bcResearchTabs-label">{m.label}</span>
            <span className="bcResearchTabs-value" style={m.color ? { color: m.color } : undefined}>{m.value}</span>
          </div>
        ))}
      </div>
      {Array.isArray(seo.data.competitors) && seo.data.competitors[0] && (
        <div className="bcResearchTabs-detail">
          <span className="bcResearchTabs-label">
            <i className="fa fa-users" /> Top Competitor
          </span>
          <a
            href={`https://${seo.data.competitors[0].domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bcResearchTabs-value bcResearchTabs-link"
          >
            {seo.data.competitors[0].domain}
          </a>
        </div>
      )}
      {seo.scannedAt && (
        <div className="bcResearchTabs-meta">
          <i className="fa fa-clock" /> Scanned {formatDate(seo.scannedAt)}
        </div>
      )}
    </div>
  );
};

export default ResearchInfoTabs;
